/**
 * react-i18next setup (DEC-024; ch18 i18n rules R-18.316/.317/.318/.320/.322/.325).
 *
 * - react-i18next is the mandated i18n library for React (Vite) projects (R-18.316).
 * - Configured here at `src/shared/lib/i18n.ts` and imported as a side effect from the
 *   app entry point (R-18.317).
 * - `common` is the default namespace loaded at startup (R-18.318).
 * - Translation files are served at `public/locales/{{lng}}/{{ns}}.json` over the HTTP
 *   backend (R-18.320); feature namespaces are lazy-loaded on first use (R-18.319) by
 *   M9-M11.
 * - English is the default + fallback; Latvian is the second locale (DEC-024). Missing
 *   keys fall back to EN (R-18.313) and warn in development (R-18.308).
 * - Locale detection uses the cookie/navigator detector for this internal tool
 *   (R-18.322) and persists the choice to a cookie (R-18.325).
 * - `document.documentElement.lang` is kept in sync with the active language — set on
 *   init and on every `languageChanged` event — so assistive tech announces content in
 *   the correct voice (WCAG 2.1 AA, SC 3.1.1/3.1.2). The static `lang` in index.html is
 *   only a pre-hydration default.
 *
 * All user-facing strings must come from these files — no hardcoded JSX literals
 * (R-18.310, DEC-024).
 */

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

export const SUPPORTED_LOCALES = ['en', 'lv'] as const;
export const DEFAULT_LOCALE = 'en';

void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES,
    defaultNS: 'common',
    ns: ['common'],
    load: 'languageOnly',
    debug: import.meta.env.DEV,
    interpolation: {
      // React already escapes output, so i18next must not double-escape.
      escapeValue: false,
    },
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
      order: ['cookie', 'navigator'],
      caches: ['cookie'],
      lookupCookie: 'i18next',
    },
  });

/** Keep the document language attribute in sync with the active i18next language. */
function syncDocumentLang(lng: string): void {
  document.documentElement.lang = lng;
}

// Set the initial value once i18next has resolved a language, then track every change.
syncDocumentLang(i18n.resolvedLanguage ?? DEFAULT_LOCALE);
i18n.on('languageChanged', syncDocumentLang);

export { i18n };
